import { useStaticQuery, graphql } from "gatsby";
import Markdown from "markdown-to-jsx";
import dayjs from "dayjs";

import Heading from "@/components/elements/Heading";
import Link from "@/components/elements/LinkComponent";
import Icon from "@/components/elements/Icon";
import Image from "@/components/elements/Image";
import ImagePlaceholder from "@/components/elements/ImagePlaceholder";
import LinkComponent from "@/components/elements/LinkComponent";

import { IconName } from "@/components/elements/Icon/Icon";
import * as styles from "./styles.module.scss";

export function FooterMain({
  location_info,
  numbers_info,
  contact_info,
  main_menu,
  group_menu,
  copyrights_menu,
}: Pick<
  FooterTemplate,
  | "location_info"
  | "numbers_info"
  | "contact_info"
  | "main_menu"
  | "group_menu"
  | "copyrights_menu"
>) {
  const { news } = useStaticQuery<{
    news: {
      edges: {
        node: StoryblokStory;
      }[];
    };
  }>(graphql`
    query FooterQuery {
      news: allStoryblokEntry(
        filter: { field_component: { eq: "template_scientificUpdate" } }
        sort: { fields: field_su_date_of_publication_string, order: DESC }
        limit: 4
      ) {
        edges {
          node {
            content
            full_slug
          }
        }
      }
    }
  `);

  const parsedNews =
    news?.edges.map(({ node }) => ({
      ...(JSON.parse(node.content) as ScientificUpdatePageTemplate),
      full_slug: node.full_slug,
    })) || [];

  const contactData = [
    {
      icon: "mail",
      label: "Contact info",
      data: contact_info,
    },
    {
      icon: "location",
      label: "Our location",
      data: location_info,
    },
    { icon: "building", label: "Organisation number", data: numbers_info },
  ];

  return (
    <div className={styles.footerMainContainer}>
      <div className={styles.contactContainer}>
        {contactData.map((item) => (
          <div key={item.label} className={styles.contactItem}>
            <div className={styles.contactItemHead}>
              <Icon width={24} height={25} name={item.icon as IconName} />
              <Heading level={6}>{item.label}</Heading>
            </div>
            <div className={styles.contactItemContent}>
              {item.data && <Markdown>{item.data}</Markdown>}
            </div>
          </div>
        ))}
      </div>
      <div className={styles.linksContainer}>
        <div>
          {Array.isArray(group_menu) &&
            group_menu.map((group) => (
              <ul key={group.label}>
                <li>{group.label}</li>
                {Array.isArray(group.menu) &&
                  group.menu.map((link) => {
                    return (
                      <li key={link._uid}>
                        <Link
                          className={styles.standardLink}
                          type={link.type}
                          url={link.url}
                          title={link.title}
                        >
                          {link.title}
                        </Link>
                      </li>
                    );
                  })}
              </ul>
            ))}
        </div>
        <div>
          {Array.isArray(main_menu) && (
            <ul className={styles.comingSoonMenu}>
              {main_menu.map((link) => {
                return (
                  <li key={link._uid}>
                    <Link type={link.type} url={link.url}>
                      {link.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {Array.isArray(copyrights_menu) && (
            <ul>
              {copyrights_menu.map((link) => {
                return (
                  <li key={link._uid}>
                    <Link type={link.type} url={link.url}>
                      {link.title}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className={styles.news}>
        <LinkComponent type="internal" url="/scientific-updates">
          <Heading level={6}>Scientific updates</Heading>
        </LinkComponent>

        <div className={styles.newsContainer}>
          {parsedNews.map((item) => (
            <LinkComponent
              url={`/${item.full_slug}`}
              key={item._uid}
              className={styles.newsItem}
              type="internal"
            >
              <div className={styles.thumbnail}>
                {item.su_hero_img?.image ? (
                  <Image
                    data={item.su_hero_img}
                    fluid
                    options={{
                      maxWidth: 160,
                    }}
                  />
                ) : (
                  <ImagePlaceholder />
                )}
              </div>
              <div className={styles.newsItemContent}>
                <p>{item.su_title}</p>
                <span>
                  {dayjs(item.su_date_of_publication).format("DD MMMM YYYY")}
                </span>
              </div>
            </LinkComponent>
          ))}
        </div>
      </div>
    </div>
  );
}
